import React, { useLayoutEffect, useRef, useState } from 'react';
import { Router } from 'react-router-dom';
import { createHashHistory } from '@remix-run/router';
import { hasManagedUnsavedChanges, requestDiscardUnsavedChanges } from '../utils/unsavedChanges';

// Keep the current route mounted while an owned draft answers a Back/Forward
// request. A window popstate listener alone runs after HashRouter's listener.
export default function GuardedHashRouter({ children, basename, future }) {
  const historyRef = useRef(null);
  if (!historyRef.current) historyRef.current = createHashHistory({ v5Compat: true });
  const history = historyRef.current;
  const [state, setState] = useState({ action: history.action, location: history.location });
  useLayoutEffect(() => {
    let alive = true, restoring = false, pending = null;
    let acceptedState = window.history.state, acceptedUrl = window.location.href;
    const accept = update => {
      acceptedState = window.history.state; acceptedUrl = window.location.href;
      setState({ action: update.action, location: update.location });
    };
    const ask = () => {
      const attempt = pending;
      requestDiscardUnsavedChanges().then(approved => {
        if (!alive || pending !== attempt) return;
        pending = null;
        if (approved) {
          if (attempt.delta) window.history.go(attempt.delta);
          else history.push(attempt.location);
        }
      });
    };
    const unlisten = history.listen(update => {
      if (restoring) {
        const remaining = window.history.state?.idx - acceptedState?.idx;
        if (Number.isFinite(remaining) && remaining) { window.history.go(-remaining); return; }
        restoring = false; ask(); return;
      }
      if (update.action !== 'POP' || !hasManagedUnsavedChanges()) { accept(update); return; }
      const delta = Number.isInteger(window.history.state?.idx) && Number.isInteger(acceptedState?.idx)
        ? window.history.state.idx - acceptedState.idx : 0;
      // Repeated Back while a dialog is open still returns to the accepted entry.
      pending = { delta, location: update.location };
      if (delta) { restoring = true; window.history.go(-delta); }
      else { window.history.replaceState(acceptedState, '', acceptedUrl); ask(); }
    });
    return () => { alive = false; unlisten(); };
  }, [history]);
  return <Router basename={basename} location={state.location} navigationType={state.action} navigator={history} future={future}>{children}</Router>;
}
